import { DeviceParameterConfig } from '@eppendorf/app-configs';
import {
  AlertState,
  Component,
  TelemetryHistoryEnrichedDTO,
} from '@eppendorf/vnls-device-service-utils';
import { Badge, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import cn from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertStatus } from '$features/monitoring/alert-status/alert-status';
import { snapshotHasThresholds } from '$features/monitoring/monitoring.utils.snapshot';
import { MonitoringParameterValueAgeSnapshot } from '$features/monitoring/parameters/snapshot/monitoring-parameter-value-age-snapshot';
import { MonitoringParameterValueSnapshot } from '$features/monitoring/parameters/snapshot/monitoring-parameter-value-snapshot';
import { ThresholdsFormSnapshot } from '$features/monitoring/parameters/snapshot/thresholds-form-snapshot';
import { findTelemValueByType } from '$features/monitoring/parameters/snapshot/utils/find-telem-value-by-type';
import {
  formatCentrifugeLargeDuration,
  resolveSetPointValue,
} from '$features/monitoring/shared/centrifuge-large.utils';
import { getCountdownTimerDisplay } from '$features/monitoring/shared/countdown-timer.utils';
import { applyFractionDigits } from '$features/monitoring/shared/formatting.utils';
import { ParameterHeadline } from '$features/monitoring/shared/parameter-headline';

import styles from '../monitoring-parameter.module.scss';

export interface MonitoringParameterSnapProps {
  name?: string;
  parameterConfig?: DeviceParameterConfig;
  processValue?: TelemetryHistoryEnrichedDTO;
  shadowComponent?: Component;
  fractionalDigits?: number | null;
  isSensor: boolean;
  deviceId: string;
  isCentrifugeLarge?: boolean;
  isThermomixer?: boolean;
  hasStaticSetpointDisplay?: boolean;
  isCountdownTimer?: boolean;
  activityState?: string;
}

/**
 * Determines whether a centrifuge-large shadow component has a multi-step setpoint.
 * When the component's state is "MultiStep" or when there are multiple setpoint values,
 * we display a placeholder text instead of the setpoint value.
 */
function isMultiStepSetpoint(shadowComponent?: Component): boolean {
  if (!shadowComponent) return false;
  // setPoint can be an array at runtime for step programs (e.g., [600, 300, 200])
  if (Array.isArray(shadowComponent.setPoint) && shadowComponent.setPoint.length > 1)
    return true;
  return false;
}

/**
 * Returns the display value for a centrifuge-large process value.
 * For time/runtime parameters, converts seconds to hh:mm:ss format.
 */
function getCentrifugeLargeDisplayValue(
  processValue?: TelemetryHistoryEnrichedDTO,
): string | undefined {
  const rawValue = findTelemValueByType(processValue);
  if (!rawValue) return undefined;

  // For Runtime/Time parameters, convert to hh:mm:ss format
  const telemetryId = processValue?.telemetryId?.toLowerCase();
  if (telemetryId === 'runtime') {
    return formatCentrifugeLargeDuration(rawValue, processValue?.valueType);
  }

  return rawValue;
}

export function MonitoringParameterSnapshot({
  name,
  parameterConfig,
  processValue,
  shadowComponent,
  isSensor,
  deviceId,
  isCentrifugeLarge: isCentrifugeLargeDevice,
  isThermomixer: isThermomixerDevice,
  hasStaticSetpointDisplay: hasStaticSetpoint,
  isCountdownTimer,
  activityState,
}: MonitoringParameterSnapProps) {
  const { t } = useTranslation();
  const [showThreshholds, setShowThresholds] = useState(false);

  const hasThresholds = snapshotHasThresholds(shadowComponent) || isSensor;
  const isShowingThresholdsAllowed =
    hasThresholds ||
    !!isCentrifugeLargeDevice ||
    !!isThermomixerDevice ||
    !!hasStaticSetpoint;

  function handleExpand(): void {
    if (isShowingThresholdsAllowed) {
      setShowThresholds(!showThreshholds);
    }
  }

  function handleKeyUp(event: React.KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Enter') {
      handleExpand();
    }
  }

  const aggrAlertState = shadowComponent?.alertState as AlertState | undefined;

  // Thermomixer has no thresholds — suppress alert status coloring
  const effectiveAlertState = isThermomixerDevice ? undefined : aggrAlertState;

  // Thermomixer: override fractional digits to 0 (no decimals)
  const effectiveFractionalDigits = isThermomixerDevice
    ? 0
    : processValue?.fractionalDigits;
  const effectiveShadowFractionalDigits = isThermomixerDevice
    ? 0
    : shadowComponent?.fractionalDigits;

  // Centrifuge-large specific: determine if multi-step setpoint
  const showMultiStepPlaceholder =
    isCentrifugeLargeDevice && isMultiStepSetpoint(shadowComponent);

  // For centrifuge-large Runtime, don't show unit (it's embedded in the time format)
  const isRuntimeParam = processValue?.telemetryId?.toLowerCase() === 'runtime';
  const isSpeedParam = processValue?.telemetryId?.toLowerCase() === 'speed';

  // CS220 Agitation Timer: remaining time instead of elapsed, "--:--" when switched off
  const countdownTimer = isCountdownTimer
    ? getCountdownTimerDisplay(findTelemValueByType(processValue), shadowComponent)
    : undefined;

  // Get display value (CS220 Agitation Timer: remaining time, centrifuge-large: formatted)
  const displayValue = (() => {
    if (countdownTimer) return countdownTimer.value;
    if (!isCentrifugeLargeDevice) {
      return findTelemValueByType(processValue);
    }
    return getCentrifugeLargeDisplayValue(processValue);
  })();

  // Speed unit: device always reports values in rpm; unit may be empty until metadata is processed
  const resolvedUnit = (() => {
    if (isCentrifugeLargeDevice && isRuntimeParam) return null;
    if (isCentrifugeLargeDevice && isSpeedParam && !shadowComponent?.unit) return 'rpm';
    return shadowComponent?.unit;
  })();

  // Centrifuge-large: resolve array setpoints and format Runtime as time
  const setPointValue = (() => {
    if (countdownTimer) return countdownTimer.setPoint;
    if (!isCentrifugeLargeDevice) return shadowComponent?.setPoint;
    // Device sends setPoint as number or array (e.g., [600] single step, [600, 300] multi-step)
    const resolved = resolveSetPointValue(shadowComponent?.setPoint);
    if (isRuntimeParam && resolved) {
      return formatCentrifugeLargeDuration(resolved, shadowComponent?.valueType);
    }
    return resolved;
  })();

  // Centrifuge-large: label switches between "Current value" (running) and "Value" (idle/other)
  const isRunning = ['Running', 'AutomaticallyRunning', 'ManuallyRunning'].includes(
    activityState ?? '',
  );
  const valueLabel = isRunning
    ? t('alertThresholds.valueHint')
    : t('alertThresholds.valueHintOldTimestamp');

  return (
    <div className={cn(`box`, styles.monitoringParameterBox)}>
      <div
        role="button"
        tabIndex={0}
        className={cn(styles.monitoringParameterBoxHead, {
          interactive: isShowingThresholdsAllowed,
        })}
        onClick={handleExpand}
        onKeyUp={handleKeyUp}
      >
        <MonitoringParameterValueAgeSnapshot
          timestamp={processValue?.sentAt}
          className={styles.monitoringParameterBoxTimestamp}
        />
        <ParameterHeadline
          parameterName={name}
          parameterConfig={parameterConfig}
          className={styles.monitoringParameterBoxHeadline}
        />
        <div className={styles.monitoringParameterBoxCurrentValues}>
          {!showThreshholds && (
            <>
              <div className={styles.monitoringParameterBoxValueWrapper}>
                <AlertStatus
                  alertStatus={effectiveAlertState}
                  className={styles.monitoringParameterBoxValueAlertStatus}
                />
                {displayValue ? (
                  <MonitoringParameterValueSnapshot
                    data-testid={`process-value-${processValue?.telemetryId}`}
                    value={displayValue}
                    unit={resolvedUnit}
                    valueType={
                      isCentrifugeLargeDevice && isRuntimeParam
                        ? 'STRING'
                        : processValue?.valueType
                    }
                    status={effectiveAlertState}
                    formatter={applyFractionDigits(effectiveFractionalDigits)}
                    className={styles.monitoringParameterBoxValue}
                  />
                ) : (
                  <Badge size="small">{t('monitoringDetail.noLiveData')}</Badge>
                )}
              </div>
              {showMultiStepPlaceholder ? (
                <span className={styles.monitoringParameterBoxAdditionalInfo}>
                  {t('monitoringDetail.multiStepSetpointActive')}
                </span>
              ) : (
                <MonitoringParameterValueSnapshot
                  value={setPointValue}
                  unit={resolvedUnit}
                  valueType={
                    isCentrifugeLargeDevice && isRuntimeParam
                      ? 'STRING'
                      : (shadowComponent?.valueType ?? processValue?.valueType)
                  }
                  formatter={applyFractionDigits(effectiveShadowFractionalDigits)}
                  className={styles.monitoringParameterBoxAdditionalInfo}
                />
              )}
            </>
          )}
        </div>
        {isShowingThresholdsAllowed && (
          <div className={styles.monitoringParameterBoxToggle}>
            <Icon
              name={showThreshholds ? 'down' : 'right'}
              size={IconSizeClasses.Responsive}
              className={styles.monitoringParameterBoxToggleIcon}
            />
          </div>
        )}
      </div>
      {showThreshholds && hasThresholds && !hasStaticSetpoint && (
        <ThresholdsFormSnapshot
          alertStatus={effectiveAlertState}
          processValue={processValue}
          isSensor={isSensor}
          shadowComponent={shadowComponent}
          deviceId={deviceId}
        />
      )}
      {showThreshholds &&
        ((!hasThresholds && (isCentrifugeLargeDevice || isThermomixerDevice)) ||
          !!hasStaticSetpoint) && (
          <div className={styles.monitoringParameterExpandedDetail}>
            <div className={styles.monitoringParameterExpandedRow}>
              <span className={styles.monitoringParameterExpandedLabel}>
                {valueLabel}
              </span>
              <MonitoringParameterValueSnapshot
                value={displayValue}
                unit={resolvedUnit}
                valueType={
                  isCentrifugeLargeDevice && isRuntimeParam
                    ? 'STRING'
                    : processValue?.valueType
                }
                formatter={applyFractionDigits(effectiveFractionalDigits)}
                className={styles.monitoringParameterExpandedValue}
              />
            </div>
            <div className={styles.monitoringParameterExpandedRow}>
              <span className={styles.monitoringParameterExpandedLabel}>
                {t('alertThresholds.setPoint')}
              </span>
              {showMultiStepPlaceholder ? (
                <span className={styles.monitoringParameterExpandedSetpoint}>
                  {t('monitoringDetail.multiStepSetpointActive')}
                </span>
              ) : (
                <MonitoringParameterValueSnapshot
                  value={setPointValue}
                  unit={resolvedUnit}
                  valueType={
                    isCentrifugeLargeDevice && isRuntimeParam
                      ? 'STRING'
                      : (shadowComponent?.valueType ?? processValue?.valueType)
                  }
                  formatter={applyFractionDigits(effectiveShadowFractionalDigits)}
                  className={styles.monitoringParameterExpandedSetpoint}
                />
              )}
            </div>
          </div>
        )}
    </div>
  );
}
